import { useTracking } from '@vp/react-tracking';
import { FC, useCallback, useEffect, useState } from 'react';
import {
    Button,
    ModalDialog,
    ModalDialogBody,
    ModalDialogButtons,
    ModalDialogCloseButton,
    ModalDialogContent,
    ModalDialogFooter,
    ModalDialogHeader,
    ModalDialogNav,
    ModalDialogTitle,
    useScreenClass,
} from '@vp/swan';
import { useIdentityContext } from '@99designs/design-services-common';
import { __, __url } from '@99designs/i18n';
import { TopBar } from '@99designs/swan-wizard';
import { usePage } from '@99designs/tracking';
import { useBriefContext } from '../BriefContext';

const TRACKING_CALLER = 'brief-save-and-exit';

export interface MultiStepTopBarProps {
    title: string;
    exitUrl: string;
    hideExitPopUp?: boolean;
}

export function MultiStepTopBar({ title, exitUrl, hideExitPopUp }: MultiStepTopBarProps) {
    const {
        config: {
            showTitle,
            showHelp,
            showExit,
            tenant: { logo, logoAria, helpLabel, helpRoute },
        },
        isBifrostBrief,
    } = useBriefContext();

    // TODO: Remove isBifrostBrief check once all bifrost products are removed
    const exitButton = isBifrostBrief ? (
        <ExitButton url={exitUrl} hideExitPopUp={hideExitPopUp || false} />
    ) : (
        <SaveAndExitButton url={exitUrl} />
    );

    return (
        <TopBar
            help={showHelp}
            logo={logo}
            logoAria={logoAria}
            helpLabel={helpLabel}
            helpRoute={helpRoute}
            {...(showExit && { exit: exitButton })}
            {...(showTitle && { title: title })}
        />
    );
}

interface ExitButtonProps {
    url: string;
    hideExitPopUp: boolean;
}

export const ExitButton: FC<ExitButtonProps> = ({ url, hideExitPopUp }) => {
    const { pageData } = usePage();
    const { tracking } = useTracking();

    const screenClass = useScreenClass();

    const [isOpen, setIsOpen] = useState(false);

    const openExitModal = () => {
        setIsOpen(true);
    };

    const closeExitModal = () => {
        tracking.track('Pop-Up Closed', {
            category: 'Pop-Up',
            label: 'Design Brief Exit Modal Pop-Up',
            pageSection: pageData.pageSection,
            pageStage: pageData.pageStage,
            pageName: pageData.pageName,
        });
        setIsOpen(false);
    };

    const handleExitConfirmation = useCallback(() => {
        tracking.track('Navigation Clicked', {
            category: pageData.pageSection,
            label: 'Content: Exit Confirmation Pop-Up',
            eventDetail: `${window.location.pathname}${window.location.search};${url};Exit Confirmation Pop-Up;designBriefExitConfirmationButton`,
            navigationDetail: 'designBriefExitConfirmationButton',
            pageSection: pageData.pageSection,
            pageStage: pageData.pageStage,
            pageName: pageData.pageName,
        });

        // `.assign()` rather than `href =` — the latter requires an absolute URL under
        // jsdom's Location implementation, but `url` may be a relative path; `.assign()`
        // is spec-equivalent to `href =` (new history entry) and resolves both forms.
        window.location.assign(url);
    }, [pageData, tracking, url]);

    useEffect(() => {
        if (isOpen) {
            tracking.track('Pop-Up Viewed', {
                category: 'Pop-Up',
                label: 'Design Brief Exit Modal Pop-Up',
                pageSection: pageData.pageSection,
                pageStage: pageData.pageStage,
                pageName: pageData.pageName,
            });
        }
    }, [isOpen, pageData, tracking]);

    return (
        <>
            <Button
                skin={screenClass === 'xs' ? 'tertiary' : undefined}
                onClick={hideExitPopUp ? handleExitConfirmation : () => openExitModal()}
                data-testid="brief-exit-button"
            >
                {__('Exit')}
            </Button>
            <ModalDialog
                isOpen={isOpen}
                onRequestDismiss={closeExitModal}
                data-testid="brief-exit-modal"
            >
                <ModalDialogContent
                    aria-labelledby="exit-modal-title"
                    data-testid="brief-exit-modal-content"
                >
                    <ModalDialogNav>
                        <ModalDialogCloseButton
                            accessibleText={__('Close')}
                            data-testid="brief-exit-modal-close"
                        />
                    </ModalDialogNav>
                    <ModalDialogHeader>
                        <ModalDialogTitle
                            data-testid="brief-exit-modal-title"
                            id="exit-modal-title"
                        >
                            {__('Are you sure?')}
                        </ModalDialogTitle>
                    </ModalDialogHeader>
                    <ModalDialogBody data-testid="brief-exit-modal-body">
                        {__('Are you sure you want to exit your brief? All progress will be lost.')}
                    </ModalDialogBody>
                    <ModalDialogFooter>
                        <ModalDialogButtons>
                            <Button
                                skin="secondary"
                                onClick={closeExitModal}
                                data-testid="brief-exit-modal-go-back"
                            >
                                {__('Go back')}
                            </Button>
                            <Button
                                onClick={handleExitConfirmation}
                                skin="primary"
                                data-testid="brief-exit-modal-confirm"
                            >
                                {__('Yes, exit')}
                            </Button>
                        </ModalDialogButtons>
                    </ModalDialogFooter>
                </ModalDialogContent>
            </ModalDialog>
        </>
    );
};

function SaveAndExitButton({ url }: { url: string }) {
    const { SignIn, isSignedIn } = useIdentityContext();
    const {
        config: { exitTo },
        workId,
    } = useBriefContext();

    const { pageData } = usePage();
    const { tracking } = useTracking();

    const screenClass = useScreenClass();

    const [isOpen, setIsOpen] = useState(false);

    const studioUrl = `${__url('/studio/')}?workId=${workId}`;
    const dashboardUrl = __url('/graphic-design/dashboard?tab=draft');

    const useStudio = exitTo === 'STUDIO';
    const saveAndExitUrl = useStudio && workId ? studioUrl : dashboardUrl;

    useEffect(() => {
        if (isOpen) {
            tracking.track('Pop-Up Viewed', {
                category: 'Pop-Up',
                label: 'Design Brief Exit Modal Pop-Up',
                pageSection: pageData.pageSection,
                pageStage: pageData.pageStage,
                pageName: pageData.pageName,
            });
        }
    }, [isOpen, pageData, tracking]);

    const handleExitWithoutSavingButton = () => {
        tracking.track('Navigation Clicked', {
            category: pageData.pageSection,
            label: 'Content: Exit Confirmation Pop-Up',
            eventDetail: `${window.location.pathname}${window.location.search};${url};Exit Confirmation Pop-Up;designBriefExitConfirmationButton`,
            navigationDetail: 'designBriefExitConfirmationButton',
            pageSection: pageData.pageSection,
            pageStage: pageData.pageStage,
            pageName: pageData.pageName,
        });

        // Upload & Go briefs with a workId should return to Studio.
        // `.assign()` rather than `href =` — see handleExitConfirmation above.
        window.location.assign(useStudio && workId ? studioUrl : url);
    };

    const handleSaveAndExitButton = () => {
        // Saving as a draft is already done progressively as the user fills in the form.

        if (!isSignedIn) {
            setIsOpen(true);
        } else {
            tracking.track('Navigation Clicked', {
                category: pageData.pageSection,
                label: 'Design Brief Exit To Drafts',
                eventDetail: `${window.location.pathname}${window.location.search};${saveAndExitUrl};Design Brief Exit To Drafts;designBriefExitToDrafts`,
                navigationDetail: 'designBriefExitToDrafts',
                pageSection: pageData.pageSection,
                pageStage: pageData.pageStage,
                pageName: pageData.pageName,
            });

            // Navigates to the user's My Design Services dashboard.
            // `.assign()` rather than `href =` — see handleExitConfirmation above.
            window.location.assign(saveAndExitUrl);
        }
    };

    const handleCancel = () => {
        tracking.track('Pop-Up Closed', {
            category: 'Pop-Up',
            label: 'Design Brief Exit Modal Pop-Up',
            pageSection: pageData.pageSection,
            pageStage: pageData.pageStage,
            pageName: pageData.pageName,
        });

        setIsOpen(false);
    };

    return (
        <>
            <Button
                skin={screenClass === 'xs' ? 'tertiary' : undefined}
                onClick={handleSaveAndExitButton}
                data-testid="brief-save-and-exit-button"
            >
                {screenClass === 'xs'
                    ? __('Exit')
                    : __('Save & exit', {
                          __context:
                              'This is text on the escape hatch/exit button for a design brief form.',
                      })}
            </Button>
            <ModalDialog
                isOpen={isOpen}
                onRequestDismiss={handleCancel}
                data-testid="brief-save-and-exit-modal"
            >
                <ModalDialogContent
                    aria-labelledby="save-exit-modal-title"
                    data-testid="brief-save-and-exit-modal-content"
                >
                    <ModalDialogNav>
                        <ModalDialogCloseButton
                            accessibleText={__('Close')}
                            data-testid="brief-save-and-exit-modal-close"
                        />
                    </ModalDialogNav>
                    <ModalDialogHeader>
                        <ModalDialogTitle
                            data-testid="brief-save-and-exit-modal-title"
                            id="save-exit-modal-title"
                        >
                            {__('Save your progress', {
                                __context:
                                    'This is the title text for the confirmation modal when exiting a brief when not signed in.',
                            })}
                        </ModalDialogTitle>
                    </ModalDialogHeader>
                    <ModalDialogBody data-testid="brief-save-and-exit-modal-body">
                        {__('Sign in to save this brief as a draft or exit without saving.', {
                            __context:
                                'This is the body text for the confirmation modal when exiting a brief when not signed in.',
                        })}
                    </ModalDialogBody>
                    <ModalDialogFooter>
                        <ModalDialogButtons>
                            <Button
                                skin="secondary"
                                onClick={handleExitWithoutSavingButton}
                                data-testid="brief-save-and-exit-modal-exit-without-saving"
                            >
                                {__('Exit without saving', {
                                    __context:
                                        'This is the text on a modal button shown to signed-out or anonymous users indicating that they wish to discard their form progress.',
                                })}
                            </Button>
                            <Button
                                onClick={() =>
                                    SignIn(
                                        useStudio && workId
                                            ? [{ key: 'redirectToStudio', value: '1' }]
                                            : [{ key: 'isRedirectToDrafts', value: '1' }],
                                        { trackingCaller: TRACKING_CALLER }
                                    )
                                }
                                skin="primary"
                                data-testid="brief-save-and-exit-modal-sign-in"
                            >
                                {__('Sign in and save', {
                                    __context:
                                        'This is the text on a modal button shown to signed-out or anonymous users indicating that they wish to sign in to save their draft brief form.',
                                })}
                            </Button>
                        </ModalDialogButtons>
                    </ModalDialogFooter>
                </ModalDialogContent>
            </ModalDialog>
        </>
    );
}
